/* AddInstructionsSummaryRenderer.js */

"use strict";

const ButtonControl = require("@gluenetworks/steve_ui_core").Control.Action.Button;

/**
 * AddInstructionsSummaryRenderer
 *
 * Renders a summary view for instruction linking results
 * Displays success/failure counts and detailed error messages
 */
class AddInstructionsSummaryRenderer {
    /**
     * Constructor for the AddInstructionsSummaryRenderer
     * @param {Object} model - The model for the component
     */
    constructor(model) {
        this._model = model;
        this._rootElem = document.createElement("add-instructions-summary-gl");
        this._rootElem.component = this._model;
        this._logger = this._model.logger;

        this._titlePanel = document.createElement("h2");
        this._titlePanel.classList.add("summary-title");

        this._summaryPanel = document.createElement("div");
        this._summaryPanel.classList.add("summary");

        this._errorPanel = document.createElement("div");
        this._errorPanel.classList.add("error-log");

        this._errorLabel = document.createElement("div");
        this._errorLabel.innerHTML = "Error Details:";
        this._errorLabel.classList.add("log-label");

        this._errorList = document.createElement("div");
        this._errorPanel.appendChild(this._errorList);

        this._fillerPanel = document.createElement("div");
        this._fillerPanel.classList.add("filler");

        this._buttonBar = document.createElement("div");
        this._buttonBar.classList.add("summary-buttons");

        this._okButton = new ButtonControl({
            label: "OK",
            darkTheme: true,
        });

        this._registerListeners();
    }

    /********************************************/
    /* START PRIVATE COMPONENT RENDERER METHODS */
    /********************************************/

    /**
     * Register all the listeners for the component
     */
    _registerListeners() {
        this._okButton.on("UserClick", (this._onOkClick = this._onOkClick.bind(this)));
    }

    /**
     * Unregister all the listeners for the component
     */
    _unregisterListeners() {
        this._okButton.removeAllListeners("UserClick");
    }

    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PUBLIC COMPONENT RENDERER METHODS */
    /******************************************/

    /**
     * Shutdown the component
     * @returns {Promise} A promise that is resolved when the component has finished shutting down
     */
    async shutdown() {
        this._unregisterListeners();
        await this._okButton.shutdown();
        this._okButton = undefined;
        this._summaryPanel = undefined;
        this._errorPanel = undefined;
        this._errorList = undefined;
        this._errorLabel = undefined;
        this._titlePanel = undefined;
        this._buttonBar = undefined;
        this._fillerPanel = undefined;
        this._logger = undefined;
        this._model = undefined;
        this._rootElem.remove();
        this._rootElem = undefined;
    }

    /**
     * Stateless method to completely rebuild this component from the _rootElem down.
     * @returns {Promise} An ES6 promise that lets the caller know when the component has finished rendering
     */
    render() {
        return new Promise((resolve) => {
            this._rootElem.innerHTML = "";
            this._rootElem.appendChild(this._titlePanel);
            this._rootElem.appendChild(this._summaryPanel);

            // Only show error panel if there are errors
            if (this._model.summaryData.errors && this._model.summaryData.errors.length > 0) {
                this._rootElem.appendChild(this._errorLabel);
                this._rootElem.appendChild(this._errorPanel);
            }

            this._rootElem.appendChild(this._fillerPanel);
            this._rootElem.appendChild(this._buttonBar);
            this._buttonBar.appendChild(this._okButton.renderer.rootElement);
            this._displaySummary();
            resolve();
        });
    }

    /**
     * Get the root element for this component
     * @returns {HTMLElement}
     */
    get rootElement() {
        return this._rootElem;
    }

    getRootElement() {
        return this.rootElement;
    }

    /**
     * Get the model for this component
     * @returns {Object}
     */
    get model() {
        return this._model;
    }

    getModel() {
        return this.model;
    }

    /*******************************************/
    /* END COMPONENT RENDERER METHODS          */
    /* START PRIVATE RENDER METHODS            */
    /******************************************/

    /**
     * Display the summary of the instruction linking results
     */
    _displaySummary() {
        const {
            totalInstructions,
            successCount,
            failureCount,
            skippedCount,
            errors,
            totalPlatforms,
            total,
        } = this._model.summaryData;

        // Set title based on results
        const title =
            failureCount === 0
                ? "Instructions Linked Successfully"
                : failureCount === totalInstructions
                ? "Instruction Linking Failed"
                : "Instruction Linking Completed with Errors";

        this._titlePanel.innerHTML = title;

        // Display summary counts
        let totalInstructionsLabel = document.createElement("span");
        totalInstructionsLabel.innerText = `Total instructions: ${totalInstructions}`;
        this._summaryPanel.appendChild(totalInstructionsLabel);

        if (totalPlatforms) {
            let totalPlatformsLabel = document.createElement("span");
            totalPlatformsLabel.innerText = `Total platforms: ${totalPlatforms}`;
            this._summaryPanel.appendChild(totalPlatformsLabel);
        }

        let successLabel = document.createElement("span");
        successLabel.innerText = `Successfully linked: ${successCount}`;
        this._summaryPanel.appendChild(successLabel);

        if (skippedCount > 0) {
            let skippedLabel = document.createElement("span");
            skippedLabel.innerText = `Skipped (already linked): ${skippedCount}`;
            this._summaryPanel.appendChild(skippedLabel);
        }

        if (failureCount > 0) {
            let failedLabel = document.createElement("span");
            failedLabel.innerText = `Failed to link: ${failureCount}`;
            failedLabel.classList.add("error-count");
            this._summaryPanel.appendChild(failedLabel);

            // Display individual error messages
            if (errors && errors.length > 0) {
                errors.forEach((error, idx) => {
                    let errorMsg = document.createElement("div");
                    errorMsg.innerHTML = `${idx + 1}. ${error}`;
                    errorMsg.classList.add("error-msg");
                    this._errorList.appendChild(errorMsg);
                });
            }
        }
    }

    /**
     * Handle the user clicking the ok button
     */
    _onOkClick() {
        this._model.sendOk();
    }

    /*******************************************/
    /* END PRIVATE RENDER METHODS              */
    /*******************************************/
}

module.exports = AddInstructionsSummaryRenderer;
